// Zentrale SEO-Schema-Definitionen für die gesamte Website

export const getOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Praxis Versichert",
  url: "https://praxis-versichert.de",
  logo: "https://praxis-versichert.de/logo.png",
  description: "Spezialisierte Cyberversicherung für Arztpraxen, Zahnarztpraxen, Facharztpraxen, MVZ und Kliniken in Deutschland",
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+49-163-6389945",
    contactType: "customer service",
    email: "info@praxis-versichert.de",
    availableLanguage: "de"
  },
  address: {
    "@type": "PostalAddress",
    addressCountry: "DE"
  },
  sameAs: [
    "https://praxis-versichert.de"
  ]
});

export const getWebSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Praxis Versichert",
  url: "https://praxis-versichert.de",
  description: "Cyberversicherung für medizinische Praxen in Deutschland",
  potentialAction: {
    "@type": "SearchAction",
    target: "https://praxis-versichert.de/cyberversicherung?search={search_term_string}",
    "query-input": "required name=search_term_string"
  }
});

export const getBreadcrumbSchema = (items: Array<{ name: string; url?: string }>) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    ...(item.url && { item: item.url })
  }))
});

export const getLocalBusinessSchema = (city: string) => ({
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: `Praxis Versichert - Cyberversicherung ${city}`,
  url: `https://praxis-versichert.de/cyberversicherung/${city.toLowerCase()}`,
  description: `Spezialisierte Cyberversicherung für Arztpraxen in ${city}`,
  address: {
    "@type": "PostalAddress",
    addressLocality: city,
    addressCountry: "DE"
  },
  areaServed: {
    "@type": "City",
    name: city
  },
  priceRange: "$$"
});

export const getServiceSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Cyberversicherung für Arztpraxen",
  provider: {
    "@type": "Organization",
    name: "Praxis Versichert"
  },
  areaServed: "Deutschland",
  description: "Umfassende Cyberversicherung speziell für medizinische Einrichtungen"
});

export const getFAQPageSchema = (faqs: Array<{ question: string; answer: string }>) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(faq => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer
    }
  }))
});

export const getInsuranceProductSchema = (config: {
  name: string;
  description: string;
  url: string;
  brand: string;
  rating?: {
    value: number;
    count: number;
  };
  priceRange?: string;
  image?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: config.name,
  description: config.description,
  url: config.url,
  brand: {
    "@type": "Brand",
    name: config.brand
  },
  image: config.image || "https://praxis-versichert.de/logo.png",
  ...(config.rating && {
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: config.rating.value,
      reviewCount: config.rating.count,
      bestRating: "5",
      worstRating: "1"
    }
  }),
  ...(config.priceRange && {
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "EUR",
      lowPrice: config.priceRange.split('-')[0],
      highPrice: config.priceRange.split('-')[1] || config.priceRange.split('-')[0],
      availability: "https://schema.org/InStock"
    }
  }),
  category: "Cyberversicherung"
});

export const getTariffServiceSchema = (config: {
  companyName: string;
  serviceType: string;
  description: string;
  priceRange?: string;
  url: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "FinancialProduct",
  "@id": config.url,
  "name": `${config.companyName} ${config.serviceType}`,
  "description": config.description,
  "provider": {
    "@type": "InsuranceAgency",
    "name": "Praxis Versichert",
    "url": "https://praxis-versichert.de",
    "telephone": "+4917670790268",
    "areaServed": "DE"
  },
  "category": "Cyberversicherung",
  "feesAndCommissionsSpecification": config.priceRange || "€€",
  "url": config.url
});

export const getBlogSchema = (posts?: Array<{ title: string; slug: string; created_at: string }>) => ({
  "@context": "https://schema.org",
  "@type": "Blog",
  "name": "Cyberversicherung Blog",
  "description": "Aktuelle Artikel und Ratgeber zu Cyberversicherungen für Arztpraxen",
  "url": "https://praxis-versichert.de/blog",
  "publisher": {
    "@type": "Organization",
    "name": "Praxis Versichert",
    "url": "https://praxis-versichert.de"
  },
  ...(posts && posts.length > 0 && {
    "blogPost": posts.slice(0, 10).map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "url": `https://praxis-versichert.de/blog/${post.slug}`,
      "datePublished": post.created_at
    }))
  })
});

export const getGlossarySchema = (terms?: Array<{ name: string; slug: string; shortDef: string }>) => ({
  "@context": "https://schema.org",
  "@type": "DefinedTermSet",
  "name": "Cyber-Sicherheit Glossar für Arztpraxen",
  "description": "Umfassendes Lexikon zu Cybersecurity, DSGVO, IT-Sicherheit und Cyberversicherung für medizinische Einrichtungen",
  "url": "https://praxis-versichert.de/glossar",
  "publisher": {
    "@type": "Organization",
    "name": "Praxis Versichert",
    "url": "https://praxis-versichert.de"
  },
  ...(terms && terms.length > 0 && {
    "hasDefinedTerm": terms.map(term => ({
      "@type": "DefinedTerm",
      "name": term.name,
      "description": term.shortDef,
      "url": `https://praxis-versichert.de/glossar/${term.slug}`
    }))
  })
});

export const getGlossaryTermSchema = (term: {
  name: string;
  shortDef: string;
  longDescription: string;
  slug: string;
  category: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "DefinedTerm",
  "name": term.name,
  "description": term.shortDef,
  "url": `https://praxis-versichert.de/glossar/${term.slug}`,
  "termCode": term.slug,
  "inDefinedTermSet": {
    "@type": "DefinedTermSet",
    "name": "Cyber-Sicherheit Glossar",
    "url": "https://praxis-versichert.de/glossar"
  },
  "additionalType": `https://praxis-versichert.de/glossar/kategorie/${term.category}`
});

export const getBlogPostingSchema = (post: {
  title: string;
  excerpt: string;
  slug: string;
  created_at: string;
  updated_at?: string;
  featured_image?: string;
  author?: string;
  content?: string;
}) => {
  const wordCount = post.content 
    ? post.content.replace(/<[^>]*>/g, '').split(/\s+/).filter(word => word.length > 0).length 
    : undefined;

  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "image": post.featured_image || "https://praxis-versichert.de/logo.png",
    "datePublished": post.created_at,
    "dateModified": post.updated_at || post.created_at,
    "author": {
      "@type": "Person",
      "name": post.author || "Thomas Dewein",
      "url": "https://praxis-versichert.de/team"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Praxis Versichert",
      "logo": {
        "@type": "ImageObject",
        "url": "https://praxis-versichert.de/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://praxis-versichert.de/blog/${post.slug}`
    },
    ...(wordCount && { "wordCount": wordCount })
  };
};

export const getPersonSchema = (person: {
  name: string;
  position: string;
  email: string;
  phone: string;
  bio: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "name": person.name,
  "jobTitle": person.position,
  "email": person.email,
  "telephone": person.phone,
  "description": person.bio,
  "worksFor": {
    "@type": "Organization",
    "name": "Praxis Versichert",
    "url": "https://praxis-versichert.de"
  },
  "url": "https://praxis-versichert.de/team"
});

export const getConsultationEventSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Event",
  "name": "Kostenlose Cyberversicherungs-Beratung für Arztpraxen",
  "description": "Unverbindliche, persönliche Beratung zu Cyberversicherungen speziell für medizinische Einrichtungen",
  "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode",
  "eventStatus": "https://schema.org/EventScheduled",
  "location": {
    "@type": "VirtualLocation",
    "url": "https://praxis-versichert.de/termin-buchen"
  },
  "organizer": {
    "@type": "Organization",
    "name": "Praxis Versichert",
    "url": "https://praxis-versichert.de"
  },
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "EUR",
    "availability": "https://schema.org/InStock",
    "validFrom": new Date().toISOString()
  },
  "isAccessibleForFree": true
});

export const getHowToSchema = (config: {
  name: string;
  description: string;
  steps: Array<{ name: string; text: string }>;
}) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": config.name,
  "description": config.description,
  "step": config.steps.map((step, index) => ({
    "@type": "HowToStep",
    "position": index + 1,
    "name": step.name,
    "text": step.text
  }))
});

export const getItemListSchema = (items: Array<{
  name: string;
  url: string;
  position: number;
}>) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  "itemListElement": items.map(item => ({
    "@type": "ListItem",
    "position": item.position,
    "name": item.name,
    "url": item.url
  }))
});

export const getAggregateRatingSchema = (config: {
  ratingValue: number;
  reviewCount: number;
  bestRating?: number;
  worstRating?: number;
}) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Praxis Versichert",
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": config.ratingValue,
    "reviewCount": config.reviewCount,
    "bestRating": config.bestRating || 5,
    "worstRating": config.worstRating || 1
  }
});

// Table Schema for Tariff Comparison
export const getTariffComparisonTableSchema = (providers: Array<{
  name: string;
  slug: string;
  rating: string;
  highlight: string;
  category: string;
}>) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "Cyberversicherung Anbieter Vergleich für Arztpraxen",
  "description": "Vergleich der besten Cyberversicherungen für medizinische Einrichtungen",
  "numberOfItems": providers.length,
  "itemListElement": providers.map((provider, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "item": {
      "@type": "Product",
      "name": `${provider.name} Cyberversicherung`,
      "description": provider.highlight,
      "category": "Cyberversicherung",
      "brand": {
        "@type": "Brand",
        "name": provider.name
      },
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "url": `https://praxis-versichert.de/tarifwissen/${provider.slug}`
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": provider.rating === "Ausgezeichnet" ? "4.9" : provider.rating === "Sehr gut" ? "4.7" : "4.5",
        "bestRating": "5",
        "worstRating": "1"
      }
    }
  }))
});

// Coverage Areas Comparison Schema (Table Format)
export const getCoverageComparisonSchema = (config: {
  companyName: string;
  coverageAreas: Array<{
    title: string;
    items: string[];
  }>;
}) => ({
  "@context": "https://schema.org",
  "@type": "Table",
  "name": `${config.companyName} Cyberversicherung Leistungsübersicht`,
  "description": `Detaillierte Übersicht der Versicherungsleistungen der ${config.companyName} Cyberversicherung`,
  "about": {
    "@type": "Thing",
    "name": "Versicherungsleistungen",
    "description": config.coverageAreas.map(area => 
      `${area.title}: ${area.items.join(", ")}`
    ).join(" | ")
  }
});

// Insurance Conditions Table Schema
export const getInsuranceConditionsTableSchema = (config: {
  companyName: string;
  conditions: Array<{
    title: string;
    description: string | string[];
  }>;
}) => ({
  "@context": "https://schema.org",
  "@type": "Table",
  "name": `${config.companyName} Versicherungsbedingungen`,
  "description": `Übersicht der Versicherungsbedingungen der ${config.companyName} Cyberversicherung`,
  "about": {
    "@type": "Thing",
    "name": "Versicherungsbedingungen",
    "description": config.conditions.map(condition => 
      `${condition.title}: ${Array.isArray(condition.description) ? condition.description.join(" ") : condition.description}`
    ).join(" | ")
  }
});

// Target Groups Schema (Table Format)
export const getTargetGroupsSchema = (config: {
  companyName: string;
  targetGroups: Array<{
    title: string;
    description: string;
    benefits: string[];
    recommendation: string;
  }>;
}) => ({
  "@context": "https://schema.org",
  "@type": "Table",
  "name": `${config.companyName} Zielgruppen und Empfehlungen`,
  "description": `Für welche medizinischen Einrichtungen eignet sich die ${config.companyName} Cyberversicherung`,
  "about": {
    "@type": "Thing",
    "name": "Zielgruppen",
    "description": config.targetGroups.map(group => 
      `${group.title}: ${group.description} - ${group.recommendation}`
    ).join(" | ")
  }
});

// Tarif Overview ItemList Schema (Table of Contents)
export const getTarifOverviewSchema = (providers: Array<{
  name: string;
  slug: string;
  rating: string;
  highlight: string;
  category: string;
}>) => {
  // Rating Mapping
  const ratingMap: Record<string, string> = {
    "Ausgezeichnet": "4.9",
    "Testsieger": "4.9",
    "Sehr gut": "4.7",
    "Gut": "4.5"
  };

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Cyberversicherung Anbieter-Vergleich 2025 für Arztpraxen",
    "description": "Unabhängiger Vergleich aller 16 führenden Cyberversicherungs-Anbieter in Deutschland - Premium, Spezialisten und Standard-Tarife",
    "url": "https://praxis-versichert.de/tarifwissen",
    "numberOfItems": providers.length,
    "itemListOrder": "https://schema.org/ItemListOrderDescending",
    "itemListElement": providers.map((provider, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "@id": `https://praxis-versichert.de/tarifwissen/${provider.slug}`,
        "name": `${provider.name} Cyberversicherung`,
        "description": provider.highlight,
        "url": `https://praxis-versichert.de/tarifwissen/${provider.slug}`,
        "category": `Cyberversicherung ${provider.category}`,
        "brand": {
          "@type": "Brand",
          "name": provider.name
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": ratingMap[provider.rating] || "4.5",
          "bestRating": "5",
          "worstRating": "1"
        },
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "url": `https://praxis-versichert.de/tarifwissen/${provider.slug}`
        }
      }
    }))
  };
};

// Tarif Overview WebPage Schema
export const getTarifOverviewWebPageSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": "https://praxis-versichert.de/tarifwissen",
  "name": "Cyberversicherung Anbieter-Vergleich für Arztpraxen",
  "description": "Unabhängiger Vergleich aller 16 Cyberversicherungs-Anbieter mit detaillierten Leistungsübersichten",
  "url": "https://praxis-versichert.de/tarifwissen",
  "inLanguage": "de-DE",
  "isPartOf": {
    "@type": "WebSite",
    "@id": "https://praxis-versichert.de",
    "name": "Praxis Versichert"
  },
  "about": {
    "@type": "Thing",
    "name": "Cyberversicherung für medizinische Einrichtungen"
  },
  "primaryImageOfPage": {
    "@type": "ImageObject",
    "url": "https://praxis-versichert.de/assets/tarifwissen-data-hero.jpg"
  },
  "datePublished": "2024-01-15",
  "dateModified": new Date().toISOString().split('T')[0]
});

// Case Studies Schema (Reviews/Testimonials)
export const getCaseStudiesSchema = (config: {
  companyName: string;
  caseStudies: Array<{
    title: string;
    description: string;
    outcome: string;
    damage: string;
  }>;
}) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": `${config.companyName} Schadensfälle und Erfahrungen`,
  "description": `Praxisbeispiele erfolgreicher Schadensregulierungen durch ${config.companyName}`,
  "itemListElement": config.caseStudies.map((caseStudy, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "item": {
      "@type": "Review",
      "reviewBody": `${caseStudy.description} ${caseStudy.outcome}`,
      "name": caseStudy.title,
      "itemReviewed": {
        "@type": "Product",
        "name": `${config.companyName} Cyberversicherung`
      },
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": "5",
        "bestRating": "5"
      }
    }
  }))
});
